import { BitkitMarkdownCard, BitkitMarkdownCardProps, rem } from '@bitrise/bitkit-v2';

interface SuboptimalGradleCacheGuideInfoBoxProps {
  collapsedHeight?: string;
  status?: BitkitMarkdownCardProps['status'];
}

export const SuboptimalGradleCacheGuideInfoBox = ({
  collapsedHeight = rem(280),
  status = 'info',
}: SuboptimalGradleCacheGuideInfoBoxProps) => (
  <BitkitMarkdownCard collapsedHeight={collapsedHeight} status={status}>
    {text}
  </BitkitMarkdownCard>
);

const text = `

## Guide for quick Gradle Build Cache optimization

The order is based on required effort, starting with the quickest and most effective.

### Introduction

Every Gradle build is composed of tasks. Examples of these are \`javaPreCompile\`, \`compileKotlin\`, or \`kaptKotlin\`. Every task has inputs and outputs. Gradle build cache saves the fingerprint of the inputs plus the output. When a new build is run, Gradle checks each task's input fingerprint if it has a match in the cache. If there is a match, Gradle skips running the task and instead reuses the previously saved output. Every reused output speeds your build up. The purpose of this guide is to increase the rate at which Gradle finds matching cache entries and is able to skip tasks.

### Version code

Changing the \`versionCode\` or \`versionName\` in \`build.gradle\` will likely cause Gradle to ignore previously cached test results, and other gradle tasks. [Learn more about mitigating the effect](https://discuss.bitrise.io/t/bitrise-build-cache-for-gradle-changing-the-apps-version/23293).

### Dynamic environment variables

Referencing dynamic environment variables in build configuration files e.g. in \`build.gradle\`. will affect the inputs of the task as well as the hash used for caching. Reference things like Bitrise CI build's build number, build ID/Slug or the git commit hash in your build config files only where it's absolutely necessary. [Learn more about it here](https://docs.gradle.org/current/userguide/build_cache_debugging.html#diagnosing_cache_miss).

### Gradle and Android Gradle Plugin versions

In Older Android Gradle Plugins, certain tasks were not cacheable e.g. [Lint wasn't cacheable before version 3.5](https://docs.gradle.org/current/userguide/caching_android_projects.html#lint). Upgrade the Android Gradle Plugin in your project. Note that [Unit Tests are cacheable](https://issuetracker.google.com/issues/115873047?pli=1#comment24) since AGP 3.6. and [Instrumented Tests are cacheable](https://issuetracker.google.com/issues/115873051#comment8) if you use Gradle Managed Devices.

### Fabric Plugin and Crashlytics

Default behaviour of Crashlytics is to treat "each version" synonymous with "each build". This makes each build unique and breaks the cacheability of certain tasks in the build. Fix it by simply disabling Crashlytics in "debug" builds. [Learn more about it here](https://docs.gradle.org/current/userguide/caching_android_projects.html#the_fabric_plugin_and_crashlytics).

### Jetifier

Disable Jetifier if you use it and are able to drop it. [Learn how it affects Gradle cache and how to disable it](https://adambennett.dev/2020/08/disabling-jetifier/).

### Task Configuration

If the implementation of two builds is the same, you should compare them and look for at least one different input hash. If it is a simple value property, then the configuration of the task has changed. Some of the reasons to cause this, are
- Changing the build script
- Conditionally configuring the task differently for CI or the developer builds
- Depending on a system property or an environment variable for the task configuration.
- Having an absolute path which is part of the input

[Learn more about debugging and diagnosing cache misses](https://docs.gradle.org/current/userguide/build_cache_debugging.html).

### Modularized code

When part of non-modularized code is changed, Gradle will recompile it all, as well as all related tasks. Modularize your app and Gradle will identify which modules have been changed and only rebuild those. All unchanged modules can utilize the build cache and significantly speed up the build process. [Learn to modularize here](https://developer.android.com/topic/modularization).

`;
